import React, { useEffect, useState } from 'react';
import PurchaseInvoiceTable from '@/components/purchase-invoice/table';
import PurchaseInvoiceDialog from '@/components/purchase-invoice/form-dialog';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AddedItemsSchema } from '@/routes/(pharma)/_layout/inventory/add-stock/-data/schema';
import { Supplier } from '@/gen/suppliers/common_pb';
import { PurchaseOrder } from '@/gen/purchase_order/common_pb';
import db from '@/db';
import getPharmacyID from '@/utils/pharmacy_code';
import { TaxProfile } from '@/gen/medical_master/common_pb';
import { DiscountType, InventoryItem, ItemMetadata, ItemType, Quantity } from '@/gen/inventory/common_pb';
import { CreatePurchaseInvoiceRequest } from '@/gen/purchase_invoice/create_purchase_invoice_pb';
import dayjs from 'dayjs';
import { PaymentMethod, PaymentType } from '@/gen/purchase_invoice/common_pb';
import { pharmacyClient } from '@/connect';
import { toast } from '../ui/use-toast';

const initialFormSummary = {
  invoice_number: '',
  invoice_date: undefined,
  delivery_date: undefined,
  payment_mode: '',
  payment_type: '',
  supplier_id: '',
  po_id: '',
};

const PurchaseInvoiceCreateCore = ({
  suppliers,
  purchaseOrders,
  instance_id = undefined, // Optional instance ID for tracking
  onSuccess,
  stockPointId = undefined,
}: {
  suppliers: Supplier[];
  purchaseOrders: PurchaseOrder[];
  instance_id?: number; // Optional instance ID for tracking
  onSuccess?: () => void;
  stockPointId?: string;
}) => {
  const [items, setItems] = useState<AddedItemsSchema[]>([]);
  const [dialogOpen, setDialogOpen] = useState(true); // Open by default
  const [formSummary, setFormSummary] = useState(initialFormSummary);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (instance_id) {
      db.add_stock_item.filter((a) => a.instance_id === instance_id).toArray().then((data) => {
        setItems([]);
        for (const item of data) {
          setItems(prev => [
            ...prev,
            {
              db_item_id: item.id,
              quantity: { original: item.quantity, bonus: item.bonus },
              pharmacy_code: getPharmacyID(),
              batch_number: item.batch_no || '',
              expiry_date: item.expiry_date || undefined,
              production_date: item.production_date || undefined,
              medical_master_id: item.type === ItemType.MEDICAL.toString() ? item.item_id || '' : '',
              non_medical_master_id: item.type === ItemType.NON_MEDICAL.toString() ? item.item_id || '' : undefined,
              type: (item.type === ItemType.MEDICAL.toString() ? "MEDICAL" : item.type === ItemType.NON_MEDICAL.toString() ? "NON_MEDICAL" : "MEDICAL") as "MEDICAL" | "NON_MEDICAL",
              cost: item.cost,
              price: 0,
              scientific_name: item.scientific_name || '',
              unit: item.unit || '',
              unit_type: item.unit_type || '',
              metadata: {
                custom_barcode: item.custom_barcode || '',
                discount_type: item.discount_type || '',
                discount_value: item.discount_value || 0,
              },
              trade_name: item.product_name || '',
              taxProfile: TaxProfile.fromJsonString(item.tax_profile),
            }
          ]);
        }
        // Ensure the last row is always empty for new entries
        setItems(prev => {
          return [
            ...prev,
            {
              db_item_id: Date.now(),
              quantity: { original: 0, bonus: 0 },
              pharmacy_code: getPharmacyID(),
              batch_number: '',
              expiry_date: undefined,
              production_date: undefined,
              medical_master_id: '',
              type: 'MEDICAL',
              cost: 0,
              price: 0,
              non_medical_master_id: undefined,
              metadata: { custom_barcode: '', discount_type: '', discount_value: 0 },
              trade_name: '',
              taxProfile: undefined as any,
              scientific_name: '',
              unit: '',
              unit_type: '',
            }
          ];
        })
      })
    } else {
      // Insert a default empty row if no instance_id
      setItems([{
        db_item_id: Date.now(),
        quantity: { original: 0, bonus: 0 },
        pharmacy_code: getPharmacyID(),
        batch_number: '',
        expiry_date: undefined,
        production_date: undefined,
        medical_master_id: '',
        type: 'MEDICAL',
        cost: 0,
        price: 0,
        non_medical_master_id: undefined,
        metadata: { custom_barcode: '', discount_type: '', discount_value: 0 },
        trade_name: '',
        taxProfile: undefined as any,
        scientific_name: '',
        unit: '',
        unit_type: '',
      }]);
    }
  }, [instance_id]);

  const handleChange = (idx: number, field: string, value: any) => {
    setItems(prev => {
      const updated = [...prev];
      const fields = field.split('.');
      let obj: any = updated[idx];
      for (let i = 0; i < fields.length - 1; i++) {
        obj = obj[fields[i]];
      }
      obj[fields[fields.length - 1]] = value;

      // If selecting a product in the last row, add a new row
      if (
        (field === 'medical_master_id' || field === 'non_medical_master_id') &&
        idx === prev.length - 1 &&
        value && value !== ''
      ) {
        updated.push({
          db_item_id: Date.now(),
          quantity: { original: 0, bonus: 0 },
          pharmacy_code: '',
          batch_number: '',
          expiry_date: undefined,
          production_date: undefined,
          medical_master_id: '',
          type: 'MEDICAL',
          cost: 0,
          price: 0,
          non_medical_master_id: undefined,
          metadata: { custom_barcode: '', discount_type: '', discount_value: 0 },
          trade_name: '',
          taxProfile: undefined as any,
          scientific_name: '',
          unit: '',
          unit_type: '',
        });
      }

      return updated;
    });
  };

  const handleDelete = (idx: number) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Handler for dialog submit: update summary and close dialog
  const handleDialogSubmit = (form: any) => {
    setFormSummary({
      invoice_number: form.invoice_number || '',
      invoice_date: form.invoice_date,
      delivery_date: form.delivery_date,
      payment_mode: form.payment_mode || '',
      payment_type: form.payment_type || '',
      supplier_id: form.supplier_id || '',
      po_id: form.po_id || '',
    });
    setDialogOpen(false);
  };

  // Compact summary bar
  const renderSummaryBar = () => (
    <div className="flex items-center px-2 gap-x-4 bg-gray-100 border-y-[0.5px] py-2 my-2 rounded text-xs flex-wrap">
      <span><b>Invoice #:</b> {formSummary.invoice_number || '-'}</span>
      <span><b>Invoice Date:</b> {formSummary.invoice_date ? new Date(formSummary.invoice_date).toLocaleDateString() : '-'}</span>
      <span><b>Delivery Date:</b> {formSummary.delivery_date ? new Date(formSummary.delivery_date).toLocaleDateString() : '-'}</span>
      <span><b>Payment Mode:</b> {formSummary.payment_mode || '-'}</span>
      <span><b>Payment Type:</b> {formSummary.payment_type || '-'}</span>
      <span><b>Supplier:</b> {
        suppliers.find(s => s.id === formSummary.supplier_id)?.name || '-'
      }</span>
      <span><b>PO:</b> {
        purchaseOrders.find(po => po.id === formSummary.po_id)?.orderNumber || '-'
      }</span>
      <button
        className="ml-2 p-1 rounded hover:bg-gray-200"
        onClick={() => setDialogOpen(true)}
        title="Edit"
        type="button"
      >
        <Pencil size={16} />
      </button>
    </div>
  );

  return (
    <>
      <PurchaseInvoiceDialog open={dialogOpen} setOpen={setDialogOpen} onSubmit={handleDialogSubmit} suppliers={suppliers} purchaseOrders={purchaseOrders} instance_id={instance_id}/>
      {renderSummaryBar()}
      <div className="flex-1 min-h-0 pb-24">
        <PurchaseInvoiceTable
          items={items}
          onChange={handleChange}
          onDelete={handleDelete}
        />
      </div>
      {/* Fixed bottom modal bar */}
      <div className="fixed bottom-0 left-0 w-full border-t px-4 py-3 bg-white flex items-center justify-end gap-2 z-50">
        <Button
          variant="outline"
          type="button"
          onClick={() => { /* Save Draft handler */ }}
        >
          Save Draft
        </Button>
        <Button
          variant="default"
          className="bg-blue-700"
          type="button"
          disabled={loading || items.length === 0 || !formSummary.invoice_number || !formSummary.invoice_date || !formSummary.delivery_date || !formSummary.payment_mode || !formSummary.payment_type || !formSummary.supplier_id}
          onClick={async () => { 
            try {
              setLoading(true);
              const data = new CreatePurchaseInvoiceRequest();
              data.invoiceDate = dayjs(formSummary.invoice_date).unix()
              data.deliveryDate = dayjs(formSummary.delivery_date).unix()
              data.invoiceNumber = formSummary.invoice_number
              data.paymentMethod = formSummary.payment_mode === "CASH" ? PaymentMethod.CASH : formSummary.payment_mode === "BANK_TRANSFER" ? PaymentMethod.BANK_TRANSFER : formSummary.payment_mode === "CHEQUE" ? PaymentMethod.CHEQUE : PaymentMethod.BANK_TRANSFER;
              data.paymentType = formSummary.payment_type === "CREDIT" ? PaymentType.CREDIT : PaymentType.INSTANT;
              data.purchaseOrderId = formSummary.po_id || ""
              data.supplierId = formSummary.supplier_id!;
              const addedItems: InventoryItem[] = [];
              for (let item of items) {
                if (!item.medical_master_id && !item.non_medical_master_id) {
                  continue;
                }
                const newItem = new InventoryItem()
                newItem.masterId = item.type === "MEDICAL" ? item.medical_master_id! : item.non_medical_master_id!;
                newItem.batchNumber = item.batch_number;
                newItem.costFromSupplier = item.cost;
                newItem.sellingPrice = item.price || 0;
                newItem.itemType = item.type === "MEDICAL" ? ItemType.MEDICAL : ItemType.NON_MEDICAL;
                newItem.productionDate = item.production_date ? BigInt(dayjs(item.production_date).unix()) : BigInt(0);
                newItem.expiryDate = item.expiry_date ? BigInt(dayjs(item.expiry_date).unix()) : BigInt(dayjs().add(1, "year").unix());
                newItem.isValidExpiryDate = item.expiry_date ? dayjs(item.expiry_date).isAfter(dayjs()) : true;
                newItem.isValidProductionDate = item.production_date ? dayjs(item.production_date).isBefore(dayjs()) : true;
                
                const quantity = new Quantity()
                quantity.original = item.quantity.original;
                quantity.bonus = item.quantity.bonus;
                newItem.quantity = quantity;

                const metadata = new ItemMetadata()
                metadata.customBarcode = item.metadata.custom_barcode || "";
                metadata.discountType = parseInt(item.metadata.discount_type!) as DiscountType;
                metadata.discountValue = item.metadata.discount_value || 0;
                metadata.isDiscountable = item.metadata.discount_type !== DiscountType.NONE.toString();
                newItem.metadata = metadata;

                addedItems.push(newItem)                
              }

              data.items = addedItems;
              data.stockPointId = stockPointId || "";

              const response = await pharmacyClient.createPurchaseInvoice(data);

              // Clear the local db
              if (instance_id) {
                await db.add_stock_item.where('instance_id').equals(instance_id).delete();
                await db.create_pi_instance.where('id').equals(instance_id).delete();
                await db.create_pi_info.where('instance_id').equals(instance_id).delete();
              }

              toast({
                title: "Success",
                description: `Purchase invoice ${response.purchaseInvoiceNumber} created successfully!`,
                variant: "default",
                duration: 5000,
              });

              onSuccess?.();

            } catch (error) {
              console.error("Error creating purchase invoice:", error);
              toast({
                title: "Error",
                description: "Failed to create purchase invoice. Please try again.",
                variant: "destructive",
                duration: 5000,
              })
            } finally {
              setLoading(false);
              setItems([{
                db_item_id: Date.now(),
                quantity: { original: 0, bonus: 0 },
                pharmacy_code: getPharmacyID(),
                batch_number: '',
                expiry_date: undefined,
                production_date: undefined,
                medical_master_id: '',
                type: 'MEDICAL',
                cost: 0,
                price: 0,
                non_medical_master_id: undefined,
                metadata: { custom_barcode: '', discount_type: '', discount_value: 0 },
                trade_name: '',
                taxProfile: undefined as any,
                scientific_name: '',
                unit: '',
                unit_type: '',
              }]); // Clear items after submission
              setFormSummary(initialFormSummary); // Reset form summary
              setDialogOpen(false); // Close dialog
            }
          }}
        >
          Create Purchase Invoice
        </Button>
      </div>
    </>
  );
};

export default PurchaseInvoiceCreateCore;
